import type React from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { setFont, colorsPalettes, rgba } from "@similarweb/styles";
import { ChangeValue, PopupHoverContainer, PixelPlaceholderLoader } from "@similarweb/ui-components";

import type { ETiers } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import { getTiersMeta } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import type { RankingDistributionSingleModeData } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import {
    filterDataAccordingToRankingFilters,
    percentsFilter,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Utilities";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter, absFilter } from "app/filters/ngFilters";
import { StyledColorBadge } from "app/pages/workspace/sales/components/WebsiteDomain/styles";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { getChangeValueSign } from "./rankingDistributionSummary.utils";

import "./ranking-distribution.scss";
import { calculateChangeFromLastMonth } from "../../../../website-analysis/traffic-sources/search/common/utils";

const ItemVertical = styled.div<{ isMainItem?: boolean }>`
    display: flex;
    flex-direction: column;
`;
const Title = styled.span<{ bold?: boolean }>`
    ${setFont({ $size: 14, $color: rgba(colorsPalettes.carbon[500], 0.8) })};
    font-weight: ${({ bold }) => (bold ? 700 : 400)};
    margin-bottom: 4px;
`;
const RankingCount = styled.div`
    ${setFont({ $size: 24, $color: colorsPalettes.carbon[500], $weight: 400 })};
    margin-right: 2px;
`;

const ColorBadge = styled(StyledColorBadge)`
    position: static;
    transform: translateY(3px);
    margin-right: 9px;
`;

const SecondaryItem = styled(FlexRow)`
    margin-right: 29px;
`;
const Container = styled(FlexRow)`
    background-color: ${colorsPalettes.carbon[0]};
    padding: 32px 24px 16px 24px;
    border-radius: 6px;
`;

const NegativeTextWrapper = styled.div`
    span {
        color: ${colorsPalettes.red["s100"]};
    }
`;

const PositiveTextWrapper = styled.div`
    span {
        color: ${colorsPalettes.green["s100"]};
    }
`;

const transformDataToRankingDistributionTableData = (rankingDistributionData: RankingDistributionSingleModeData) => {
    const tiersMeta = getTiersMeta();
    const positions = Object.keys(rankingDistributionData);
    return positions.map((position, index) => {
        return {
            text: tiersMeta[index].text as string,
            id: tiersMeta[index].id as string,
            color: tiersMeta[index].color as string,
            count: rankingDistributionData[position].currentMonthNumberOfPositions as number,
            countChange:
                rankingDistributionData[position].currentMonthNumberOfPositions -
                rankingDistributionData[position].previousMonthNumberOfPositions,
            changeSincePrevMonth: !rankingDistributionData[position].previousMonthNumberOfPositions
                ? null
                : calculateChangeFromLastMonth({
                      previousMonthValue: rankingDistributionData[position].previousMonthNumberOfPositions,
                      currentMonthValue: rankingDistributionData[position].currentMonthNumberOfPositions,
                  }),
        };
    });
};

const LoadingComponent = () => {
    return (
        <FlexColumn>
            <PixelPlaceholderLoader
                width={72}
                height={16}
            />
            <div style={{ height: 6 }} />
            <PixelPlaceholderLoader
                width={95}
                height={24}
            />
        </FlexColumn>
    );
};

const RenderTooltipContent = ({
    item,
    month,
}: {
    item: {
        changeSincePrevMonth: number;
        color: string;
        count: number;
        countChange: number;
        text: string;
        id: string;
    };
    month: string;
}) => {
    const percentage = percentsFilter(item.changeSincePrevMonth);
    const count = abbrNumberFilter()(absFilter()(item.countChange));
    if (!item.changeSincePrevMonth) {
        return null;
    }
    return item.changeSincePrevMonth > 0 ? (
        <PositiveTextWrapper
            dangerouslySetInnerHTML={{
                __html: i18nFilter()("ranking.distribution.summary.tooltip.text", {
                    changeSinceLastMonth: "Gained",
                    count,
                    percentage,
                    increasingStatus: "increased",
                    positions: item.text,
                    month,
                }),
            }}
        />
    ) : (
        <NegativeTextWrapper
            dangerouslySetInnerHTML={{
                __html: i18nFilter()("ranking.distribution.summary.tooltip.text", {
                    changeSinceLastMonth: "Lost",
                    count,
                    percentage,
                    increasingStatus: "decreased",
                    positions: item.text,
                    month,
                }),
            }}
        ></NegativeTextWrapper>
    );
};

const RenderSummaryContent = ({
    item,
    month,
}: {
    item: {
        changeSincePrevMonth: number;
        color: string;
        count: number;
        countChange: number;
        text: string;
        id: string;
    };
    month: string;
}) => {
    const count = abbrNumberFilter()(item.count);
    return item.changeSincePrevMonth ? (
        <PopupHoverContainer
            content={() => (
                <RenderTooltipContent
                    item={item}
                    month={month}
                />
            )}
            config={{
                placement: "bottom",
                cssClassContent: "summary-table-tooltip",
            }}
        >
            <FlexRow>
                <ColorBadge color={item.color} />
                <ItemVertical>
                    <Title>{item.text}</Title>
                    <FlexRow alignItems="flex-end">
                        <RankingCount>{count}</RankingCount>
                        {item.changeSincePrevMonth ? (
                            <ChangeValue
                                value={percentsFilter(item.changeSincePrevMonth)}
                                {...getChangeValueSign(item.changeSincePrevMonth)}
                            />
                        ) : null}
                    </FlexRow>
                </ItemVertical>
            </FlexRow>
        </PopupHoverContainer>
    ) : (
        <>
            <ColorBadge color={item.color} />
            <ItemVertical>
                <Title>{item.text}</Title>
                <FlexRow alignItems="flex-end">
                    <RankingCount>{count}</RankingCount>
                    {item.changeSincePrevMonth ? (
                        <ChangeValue
                            value={percentsFilter(item.changeSincePrevMonth)}
                            {...getChangeValueSign(item.changeSincePrevMonth)}
                        />
                    ) : null}
                </FlexRow>
            </ItemVertical>
        </>
    );
};

export const RankingDistributionTableSummary: React.FC<{
    data?: RankingDistributionSingleModeData;
    isLoading?: boolean;
    displayedTiers: ETiers[];
    month: string;
}> = ({ data, isLoading, displayedTiers, month }) => {
    const transformedData = useMemo(() => (data ? transformDataToRankingDistributionTableData(data) : []), [data]);
    const filteredTransformData = filterDataAccordingToRankingFilters(transformedData, displayedTiers);
    return (
        <Container>
            {filteredTransformData.map((item) => {
                return (
                    <SecondaryItem
                        data-automation="ranking-distribution-summary-table-item"
                        key={item.text}
                    >
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <RenderSummaryContent
                                item={item}
                                month={month}
                            />
                        )}
                    </SecondaryItem>
                );
            })}
        </Container>
    );
};
